import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { editorOptionsBot } from '../../../query-interface/models/editor-config/editor-config';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

declare const monaco: any;

@Component({
  selector: 'app-monaco-editor-bot',
  templateUrl: './monaco-editor-bot.component.html',
  // genericTooltip = "Insert this SQL at the current cursor position in the Query Input Area.";
  styleUrl: './monaco-editor-bot.component.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MonacoEditorBotComponent), multi: true }],
})
export class MonacoEditorBotComponent implements AfterViewInit, OnDestroy {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  @Input() readOnly: boolean = false;
  @Input() expanded: boolean = false;
  @Output() codeCopied = new EventEmitter<string>();
  private editorInstance: any;
  private _value = '';

  // Required for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (_: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};
  decorationsId: string[] = [];

  ngAfterViewInit(): void {
    this.loadMonaco();
  }

  ngOnDestroy(): void {
    if (this.editorInstance) {
      this.editorInstance.dispose();
    }
  }

  private loadMonaco() {
    setTimeout(() => {
      if (!(window as any).require) {
        console.error('RequireJS not loaded. loader.js may be missing!');
        return;
      }

      (window as any).require.config({ paths: { vs: 'assets/monaco/vs' } });

      (window as any).require(['vs/editor/editor.main'], () => {
        console.log('Monaco loaded successfully!');
        this.editorInstance = monaco.editor.create(this.editorContainer.nativeElement, {
          value: this._value || '',
          readOnly: this.readOnly,
          ...editorOptionsBot,
        });

        const model = this.editorInstance.getModel();
        if (model && this._value !== model.getValue()) {
          model.setValue(this._value);
        }

        // ✨ Emit changes to form control
        this.editorInstance.onDidChangeModelContent(() => {
          const currentValue = this.editorInstance.getValue();
          if (currentValue !== this._value) {
            this._value = currentValue;
            this.onChange(currentValue);
          }
        });

        // ✨ Mark as touched on blur
        this.editorInstance.onDidBlurEditorWidget(() => {
          this.onTouched();
        });
      });
    }, 100);
  }

  // ControlValueAccessor Methods
  writeValue(value: string): void {
    this._value = value || '';
    if (this.editorInstance) {
      const model = this.editorInstance.getModel();
      if (model && this._value !== model.getValue()) {
        model.setValue(this._value);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.editorInstance) {
      this.editorInstance.updateOptions({ readOnly: isDisabled });
    }
  }

  navigateToError(line: number, column: number = 1): void {
    if (!this.editorInstance) return;

    this.editorInstance.revealPositionInCenter({ lineNumber: line, column });
    this.editorInstance.setPosition({ lineNumber: line, column });
    this.editorInstance.focus();
  }

  highlightErrorLine(line: number): void {
    if (!this.editorInstance) return;
    this.decorationsId = this.editorInstance.deltaDecorations(this.decorationsId || [], [
      {
        range: new monaco.Range(line, 1, line, 1),
        options: {
          isWholeLine: true,
        },
      },
    ]);
  }

  getQueryStartLine(): number {
    if (!this.editorInstance) return 1;
    const model = this.editorInstance.getModel();
    if (!model) return 1;
    const lines = model.getLinesContent();
    let inBlock = false;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = (lines[i] ?? '').trim();
      if (inBlock) {
        if (trimmed.includes('*/')) inBlock = false;
        continue;
      }
      if (!trimmed) continue;
      if (trimmed.startsWith('--')) continue;
      if (trimmed.startsWith('/*')) {
        if (!trimmed.includes('*/')) inBlock = true;
        continue;
      }
      return i + 1;
    }
    return 1;
  }

  translateAthenaLine(athenaLine: number): number {
    const start = this.getQueryStartLine();
    return start + athenaLine - 1;
  }
  findText() {
    console.log(typeof this.editorInstance);
    this.editorInstance.getAction('actions.find')?.run();
  }
  copyCode() {
    navigator.clipboard.writeText(this.editorInstance.getValue());
    this.codeCopied.emit(this.editorInstance.getValue());
  }
  copyFromCursorToBottom() {
    const model = this.editorInstance.getModel();
    if (!model) {
      return;
    }

    const position = this.editorInstance.getPosition();
    if (!position) {
      return;
    }

    const lastLine = model.getLineCount();
    const lastColumn = model.getLineMaxColumn(lastLine);
    const range = new monaco.Range(position.lineNumber, position.column, lastLine, lastColumn);
    const text = model.getValueInRange(range);
    navigator.clipboard.writeText(text);

    this.editorInstance.setSelection(range);
    this.editorInstance.revealRangeInCenter(range);
    this.editorInstance.focus();
    this.codeCopied.emit(text);
  }

  copyFromCursorToTop() {
    const model = this.editorInstance.getModel();
    if (!model) {
      return;
    }

    const position = this.editorInstance.getPosition();
    if (!position) {
      return;
    }

    // const lastLine = model.getLineCount();
    // const lastColumn = model.getLineMaxColumn(lastLine);
    const range = new monaco.Range(1, 1, position.lineNumber, position.column);
    const text = model.getValueInRange(range);
    navigator.clipboard.writeText(text);

    this.editorInstance.setSelection(range);
    this.editorInstance.revealRangeInCenter(range);
    this.editorInstance.focus();
    this.codeCopied.emit(text);
  }
}
